/**
 * Twin3D unitMapper — convert backend Digital Twin payload into the geometric
 * shape `floorScene` expects.
 *
 * Backend unit (GET /api/twin/:propertyId):
 *   { unit_id, tenant_id, tenant_name, floor, polygon:[[0..1]],
 *     size_sqft, status, kpis: { ocr_pct, yoy_growth_pct, footfall_share, anomaly_count } }
 *
 * Scene unit (consumed by floorScene.js):
 *   { id, unitCode, storeName, category, status, kpis,
 *     geom: { x, z, w, d, h }  // metres, top-down (XZ) }
 *
 * Plan-area sizing: ports the Sprint 3 importer prototype rule — fit the floor
 * plan inside a 28 m × 20 m max box, preserving aspect ratio (a real mall sets
 * `property.physical_dims_meters` later to override).
 */

const PLAN_MAX_W = 28;
const PLAN_MAX_D = 20;
const HEIGHT_MIN = 1.3;
const HEIGHT_MAX = 2.8;
const SQFT_REF = 6000;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Bbox of a normalized polygon. Returns null for empty / invalid. */
function polygonBbox(polygon) {
    if (!Array.isArray(polygon) || polygon.length < 3) return null;
    let minX = 1, minY = 1, maxX = 0, maxY = 0;
    for (const pt of polygon) {
        const x = Number(pt[0]);
        const y = Number(pt[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }
    if (maxX <= minX || maxY <= minY) return null;
    return { minX, minY, maxX, maxY };
}

/**
 * Derive plan area (metres) from intrinsic asset pixel dimensions.
 * Falls back to PLAN_MAX_W × PLAN_MAX_D / aspect=1.4 when unknown.
 */
export function derivePlanArea(floorPlan) {
    const w = floorPlan && Number(floorPlan.width);
    const h = floorPlan && Number(floorPlan.height);
    const aspect = (Number.isFinite(w) && Number.isFinite(h) && h > 0) ? (w / h) : 1.4;
    let pw, pd;
    if (aspect >= PLAN_MAX_W / PLAN_MAX_D) { pw = PLAN_MAX_W; pd = PLAN_MAX_W / aspect; }
    else { pd = PLAN_MAX_D; pw = PLAN_MAX_D * aspect; }
    return { w: pw, d: pd, aspect };
}

/** Convert a single backend unit to scene geometry. Returns null if no polygon. */
export function mapUnit(unit, planArea) {
    const bbox = polygonBbox(unit.polygon);
    if (!bbox) return null;

    const cx = (bbox.minX + bbox.maxX) / 2;
    const cy = (bbox.minY + bbox.maxY) / 2;
    const nw = bbox.maxX - bbox.minX;
    const nd = bbox.maxY - bbox.minY;

    const worldW = clamp(nw * planArea.w, 0.6, planArea.w);
    const worldD = clamp(nd * planArea.d, 0.6, planArea.d);

    const sqft = Number(unit.size_sqft) || 0;
    const h = sqft
        ? clamp(HEIGHT_MIN + (sqft / SQFT_REF) * (HEIGHT_MAX - HEIGHT_MIN), HEIGHT_MIN, HEIGHT_MAX)
        : 1.8;

    return {
        id: unit.unit_id,
        unitCode: unit.unit_id,
        storeName: unit.tenant_name || unit.unit_id,
        category: unit.tenant_id ? 'Tenant' : 'Vacant',
        status: unit.status || 'vacant',
        floor: unit.floor,
        kpis: unit.kpis || {},
        squareFootage: sqft,
        geom: {
            x: (cx - 0.5) * planArea.w,
            z: (cy - 0.5) * planArea.d,
            w: worldW,
            d: worldD,
            h,
        },
    };
}

/**
 * Full payload → scene-ready bundle.
 *   payload: { property: { floor_plan, ... }, units: [...] }
 *   floor:   filter to units on this floor (optional)
 */
export function mapPayloadToScene(payload, floor) {
    const property = payload?.property || {};
    const planArea = derivePlanArea(property.floor_plan);
    const units = (payload?.units || [])
        .filter((u) => floor == null || u.floor === floor)
        .map((u) => mapUnit(u, planArea))
        .filter(Boolean);
    return {
        venueName: property.name || 'Property',
        floorPlan: property.floor_plan?.url || null,
        planArea,
        units,
    };
}
